import { useCallback } from "react";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import type { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import type { AssessmentFilterOption } from "./assessmentFilterOption";
import {
  assessmentCenterFilterBarActionsSx,
  assessmentCenterFilterBarRootSx,
  assessmentCenterFilterBarScrollSx,
} from "./assessmentCenterFilterBarSx";
import AssessmentCenterRefreshLatestStatusButton from "./AssessmentCenterRefreshLatestStatusButton";
import { appDatePickerSlots } from "../shared/datePickerSlots";

export type ManualImpactAssessmentFilterBarProps = Readonly<{
  onRefresh?: () => void;
  processName: string;
  onProcessNameChange: (next: string) => void;
  status: string;
  statusOptions: AssessmentFilterOption[];
  onStatusChange: (next: string) => void;
  initiatedBy: string;
  initiatedByOptions: AssessmentFilterOption[];
  onInitiatedByChange: (next: string) => void;
  initiatedDate: Dayjs | null;
  onInitiatedDateChange: (next: Dayjs | null) => void;
}>;

export default function ManualImpactAssessmentFilterBar({
  onRefresh,
  processName,
  onProcessNameChange,
  status,
  statusOptions,
  onStatusChange,
  initiatedBy,
  initiatedByOptions,
  onInitiatedByChange,
  initiatedDate,
  onInitiatedDateChange,
}: ManualImpactAssessmentFilterBarProps) {
  const handleInitiatedDateChange = useCallback(
    onInitiatedDateChange,
    [onInitiatedDateChange],
  );

  return (
    <Box sx={assessmentCenterFilterBarRootSx}>
      <Box sx={assessmentCenterFilterBarScrollSx}>
        <TextField
          label="Process name"
          value={processName}
          onChange={(e) => onProcessNameChange(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        />

        <TextField
          select
          label="Status"
          value={status}
          onChange={(e) => onStatusChange(e.target.value)}
          sx={{ minWidth: 200 }}
          size="small"
        >
          <MenuItem value="">All</MenuItem>
          {statusOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Initiated By"
          value={initiatedBy}
          onChange={(e) => onInitiatedByChange(e.target.value)}
          sx={{ minWidth: 200 }}
          size="small"
        >
          <MenuItem value="">All</MenuItem>
          {initiatedByOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Initiated Date"
            value={initiatedDate}
            format="DD-MMM-YYYY"
            onChange={handleInitiatedDateChange}
            slots={appDatePickerSlots}
            slotProps={{
              field: {
                clearable: true,
                onClear: () => handleInitiatedDateChange(null),
              },
              textField: {
                size: "small",
                sx: { minWidth: 200 },
              },
            }}
          />
        </LocalizationProvider>
      </Box>

      <Box sx={assessmentCenterFilterBarActionsSx}>
        <AssessmentCenterRefreshLatestStatusButton onRefresh={onRefresh} />
      </Box>
    </Box>
  );
}
